import React, { FC, useMemo, useEffect, useCallback, Dispatch, SetStateAction } from 'react';
import { useHistory } from 'react-router-dom';
import { createPortal } from 'react-dom';

import { CardSchema, MessageType } from 'constants/graphqlTypes';
import { BREAKPOINTS, COLORS } from 'styles/constants';

import getStyles from 'helpers/getStyles';
import getImages from 'helpers/getImages';
import getAuthUser from 'helpers/getAuthUser';
import useTheme from 'helpers/useTheme';
import useReferrerId from 'helpers/useReferrerId';
import useOpenModal from 'helpers/useOpenModal';
import useThoughtsModal from 'helpers/useThoughtsModal';
import withHapticFeedback from 'helpers/withHapticFeedback';

import { ReactionsProvider, useReactionContextState } from 'components/Reactions/hooks';
import { useReactionMenu } from 'components/Reactions/helpers/hooks';
import ReactionMenu from 'components/Reactions/ReactionMenu';
import ReactionsModal from 'components/Reactions/ReactionsModal';
import HiddenWordsAlert from 'components/HiddenWordsAlert';
import { useIsUserAllowedToReact, useItemUrl } from 'components/Card/helpers/hooks';

import { useProfile } from 'graphQL/profile/hooks';
import { ROUTE_NICE_FIND, ROUTE_SELECT_CHAT_MODAL } from 'routes';

import { ReactComponent as AddReactionIcon } from 'assets/icons/AddReaction.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/Plus.svg';
import { ReactComponent as ThoughtsBubbleIcon } from 'assets/icons/ThoughtsBubble.svg';
import { ReactComponent as PaperPlaneIcon } from 'assets/icons/PaperPlane.svg';

import { StyledWrap } from 'components/Collection/styled';
import { PULSE_ANIMATION } from 'components/CardDetails/styled';

import useImageDimensions from './useImageDimensions';
import {
  StyledWrapper,
  StyledContainer,
  StyledImageWrap,
  StyledTextCard,
  StyledEyebrow,
  StyledSourceText,
  StyledCenteredTitle,
  StyledCommentRow,
  StyledCommentAvatar,
  StyledCommentText,
  StyledReactionTrigger,
  StyledActionButtons,
  StyledActionButton,
  StyledCardContent,
  StyledMetadata,
} from './styled';

interface ISpotlightCardProps {
  card: CardSchema;
  fontColor: string;
  isAnimationStarted?: boolean;
  isSpotlightInFocus?: boolean;
  dataTestId?: string;
  isHidden: boolean;
  setHiddenCards: Dispatch<SetStateAction<string[]>>;
  setShouldPauseAutoplay?: Dispatch<SetStateAction<boolean>>;
}

const SpotlightCard: FC<ISpotlightCardProps> = ({
  card,
  fontColor,
  isAnimationStarted,
  isSpotlightInFocus: _isSpotlightInFocus,
  dataTestId,
  isHidden,
  setHiddenCards,
  setShouldPauseAutoplay,
}) => {
  const { push } = useHistory();
  const isDark = useTheme();
  const { cardStyle, mediaTags, id, title, primarySourceDomain, primarySourceUrl, extraData, pickedByUsers } = card;

  const isUserAllowedToReact = useIsUserAllowedToReact(extraData?.items?.[0]?.user);
  const { imageUrl } = getImages(cardStyle);
  const hasImage = !!imageUrl;
  const borderColor = fontColor === COLORS.white[100] ? COLORS.white[30] : COLORS.brown.dark[30];
  const url = useItemUrl(id);
  const { userId } = getAuthUser();
  const referrerId = useReferrerId();

  // Image dimensions hook
  const { onLoad: handleImageLoad, mediaCategory, isLoaded: isImageLoaded } = useImageDimensions();

  // Comment/thought data
  const firstExtraDataItem = extraData?.items?.[0];
  const isCardReaction =
    firstExtraDataItem?.__typename !== 'CardCommentSchema' ||
    (firstExtraDataItem?.__typename === 'CardCommentSchema' && firstExtraDataItem?.isSuspicious);
  const commentText = !isCardReaction ? firstExtraDataItem?.text : null;
  const commentUserAvatarUrl = !isCardReaction
    ? firstExtraDataItem?.user?.userStyle?.thumbnailImageSet?.images?.[0]?.url
    : null;

  // Profile owner avatar as fallback (reads from cache, already fetched by ProfileTop)
  const { userData: profileOwnerData } = useProfile(referrerId);
  const profileOwnerAvatarUrl = profileOwnerData?.userStyle?.thumbnailImageSet?.images?.[0]?.url;
  const avatarUrl = commentUserAvatarUrl || profileOwnerAvatarUrl;

  // Media tag for eyebrow
  const mediaTagName = mediaTags?.items?.[0]?.name;

  // Reaction system
  const {
    reactionCoordinates,
    setReactionCoordinates,
    isQuickReactionOpen,
    setIsQuickReactionOpen,
    isReactionsModalOpen,
    setIsReactionsModalOpen,
    infoRef,
  } = useReactionMenu();

  const {
    setThoughtId,
    thoughtId,
    setPickerId,
    pickerId,
    cardId,
    setCardId,
    user,
    setUser,
  } = useReactionContextState();

  const isCurrentUserPick = useMemo(() => pickerId === userId, [pickerId, userId]);
  const showReactionButton = !isCurrentUserPick && isUserAllowedToReact;

  useEffect(() => {
    setShouldPauseAutoplay?.(isQuickReactionOpen);
  }, [isQuickReactionOpen, setShouldPauseAutoplay]);

  const extraDataItems = extraData?.items;
  const pickedByUsersItems = pickedByUsers?.items;

  useEffect(() => {
    if (!isCardReaction) {
      setThoughtId(extraDataItems?.[0]?.id ?? null);
      setUser(extraDataItems?.[0]?.user ?? null);
    }
    setCardId(card.id);
    if ((extraDataItems?.length || 0) < 2) {
      setPickerId(pickedByUsersItems?.[0]?.id ?? referrerId ?? null);
    }
  }, [
    extraDataItems,
    card.id,
    pickedByUsersItems,
    isCardReaction,
    referrerId,
    setCardId,
    setPickerId,
    setThoughtId,
    setUser,
  ]);

  useEffect(() => {
    if (isAnimationStarted && isQuickReactionOpen) {
      setIsQuickReactionOpen(false);
    }
  }, [isAnimationStarted, isQuickReactionOpen, setIsQuickReactionOpen]);

  // Reaction button click handler
  const handleReactionClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      const rect = e.currentTarget.getBoundingClientRect();
      setReactionCoordinates({ x: rect.left + rect.width / 2, y: rect.top });
      setIsQuickReactionOpen(true);
    },
    [setReactionCoordinates, setIsQuickReactionOpen]
  );

  // Action button hooks (called at top level per Rules of Hooks)
  const { userData } = useProfile();

  const goToNiceFind = useOpenModal(ROUTE_NICE_FIND, false, {
    pickId: card.pickId,
    card,
    cardImage: imageUrl,
    cardColorValue: fontColor,
    sourceUrl: primarySourceUrl,
    sourceDomainName: primarySourceDomain,
    userData,
  });
  const handlePick = withHapticFeedback(goToNiceFind);

  const openThoughts = useThoughtsModal({ id: card.pickId ?? '' });
  const handleComment = withHapticFeedback(openThoughts);

  const goToChatSelect = useOpenModal(ROUTE_SELECT_CHAT_MODAL, false, {
    entityId: card.pickId,
    messageType: MessageType.Pick,
    collectionId: null,
  });
  const handleShare = withHapticFeedback(goToChatSelect);

  // Navigation
  const handleCardClick = withHapticFeedback(() => push(url));

  // Hidden content
  const handleShowHiddenContent = () => {
    setHiddenCards?.((prev) => prev.filter((hiddenId) => hiddenId !== id));
  };

  const portalContainer = document.getElementById('spotlight-container');

  if (isHidden) {
    return (
      <HiddenWordsAlert
        showHiddenContent={handleShowHiddenContent}
        isDark={isDark}
        isSmall={!getStyles(cardStyle).layout}
        hasBottomMargin
      />
    );
  }

  return (
    <ReactionsProvider
      value={{
        isQuickReactionOpen,
        setThoughtId,
        thoughtId,
        setPickerId,
        pickerId,
        cardId,
        setCardId,
        user,
        setUser,
      }}
    >
      <StyledWrap ref={infoRef} disableLinks={isQuickReactionOpen}>
        <div className={window.innerWidth > BREAKPOINTS.m && !isCurrentUserPick ? 'no-swiping' : ''}>
          {/* Reaction menu portal */}
          {reactionCoordinates &&
            reactionCoordinates.x &&
            reactionCoordinates.y &&
            !isCurrentUserPick &&
            portalContainer &&
            createPortal(
              <ReactionMenu
                isOpen={isQuickReactionOpen}
                anchorPoint={reactionCoordinates}
                onClose={() => setIsQuickReactionOpen(false)}
                onSend={() => {}}
                onEmoji={() => {}}
                onOpenReactionsModal={() => setIsReactionsModalOpen(true)}
              />,
              portalContainer
            )}

          <StyledWrapper colorValue={fontColor}>
            <StyledCardContent>
              {/* Image or Text-only card */}
              <StyledContainer onClick={handleCardClick}>
                {hasImage ? (
                  <StyledImageWrap className={`${PULSE_ANIMATION} ${isImageLoaded ? mediaCategory : 'loading'}`}>
                    <img src={imageUrl} alt={title || ''} onLoad={handleImageLoad} />
                  </StyledImageWrap>
                ) : (
                  <StyledTextCard borderColor={borderColor}>
                    {mediaTagName && (
                      <StyledEyebrow data-testid={`${dataTestId}_type-label`}>{mediaTagName}</StyledEyebrow>
                    )}
                    <StyledCenteredTitle data-testid={`${dataTestId}_title-label`}>{title}</StyledCenteredTitle>
                    {primarySourceDomain && (
                      <StyledSourceText data-testid={`${dataTestId}_source-label`}>
                        {primarySourceDomain}
                      </StyledSourceText>
                    )}
                  </StyledTextCard>
                )}
              </StyledContainer>

              {/* Title (only for image cards -- text cards have title inside the bordered container) */}
              {hasImage && <StyledCenteredTitle data-testid={`${dataTestId}_title-label`}>{title}</StyledCenteredTitle>}

              {/* Comment row + action buttons */}
              <StyledMetadata>
                {/* Comment row: always show avatar + reaction, comment text when available */}
                <StyledCommentRow>
                  {avatarUrl && <StyledCommentAvatar src={avatarUrl} alt="" />}
                  {commentText && <StyledCommentText>&ldquo;{commentText}&rdquo;</StyledCommentText>}
                  {showReactionButton && (
                    <StyledReactionTrigger onClick={handleReactionClick}>
                      <AddReactionIcon viewBox="0 0 24 24" width={24} height={24} />
                    </StyledReactionTrigger>
                  )}
                </StyledCommentRow>

                {/* Action buttons */}
                <StyledActionButtons>
                  <StyledActionButton onClick={handlePick} data-testid={`${dataTestId}_add-btn`}>
                    <PlusIcon viewBox="0 0 24 24" width={24} height={24} />
                  </StyledActionButton>
                  <StyledActionButton onClick={handleComment} data-testid={`${dataTestId}_comment-btn`}>
                    <ThoughtsBubbleIcon viewBox="0 0 24 24" width={24} height={24} />
                  </StyledActionButton>
                  <StyledActionButton onClick={handleShare} data-testid={`${dataTestId}_share-btn`}>
                    <PaperPlaneIcon viewBox="0 0 24 24" width={24} height={24} />
                  </StyledActionButton>
                </StyledActionButtons>
              </StyledMetadata>
            </StyledCardContent>
          </StyledWrapper>

          {isReactionsModalOpen && <ReactionsModal id={card?.id} handleClose={() => setIsReactionsModalOpen(false)} />}
        </div>
      </StyledWrap>
    </ReactionsProvider>
  );
};

export default SpotlightCard;
